// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, mergeMap, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import type { Empty } from "@bufbuild/protobuf/wkt"
import { t } from "@lingui/macro"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import * as TimelineActions from "../actions"
import { executorMutation, Init, timelineService } from "../../../app/rpc"
import { DeleteTimelineMarkerRequestSchema } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type DeleteTimelineMarkerRpc = (request: Init<typeof DeleteTimelineMarkerRequestSchema>) => Observable<Empty>

export const deleteTimelineMarker = (deleteTimelineMarkerRpc: DeleteTimelineMarkerRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.deleteTimelineMarker.match),
      mergeMap(action => deleteTimelineMarkerRpc(action.payload)
        .pipe(
          mergeMap(_ =>
            concat(
              of(TimelineSlice.deleteTimelineMarker(action.payload.id ?? "")),
              of(showSnackBar({
                message: t({ id: "msg_timeline_marker_deleted", message: "Marker deleted." }),
                messageType: "SUCCESS",
              }))
            )
          ),
          catchError(handleError),
        )
      ),
    )

const deleteTimelineMarkerRpc = (request: Init<typeof DeleteTimelineMarkerRequestSchema>): Observable<Empty> =>
  executorMutation(() => timelineService.deleteTimelineMarker(create(DeleteTimelineMarkerRequestSchema, request)))

const handleError = () => {
  return of(showSnackBar({
    message: t({ id: "msg_timeline_marker_delete_failed", message: "Failed to delete marker. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default deleteTimelineMarker(deleteTimelineMarkerRpc)
